import {
  QuestionIcon,
  StarOnIcon,
} from '@pmi/dsm-react/dist/components/icons/fa/16';
import { Button, LinkButton, Popover, Selector } from '@pmi/dsm-react';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import {
  ADMIN_HELPURL,
  HUB_HELPURL,
  PLATFORM_TOUR_SESSION_STORAGE_KEY,
} from '../../../constants';
import styles from '../HelpGuides.module.scss';
import { sidenavSelector } from '../apiSlice';
import {
  hidePopupReminder,
  hideTutorial,
  platformTourSelector,
  runTutorial,
} from '../../../store/shared/platformTourSlice';

interface AdminHelpCenterMenuOption {
  label: string;
  click: () => void;
  icon: React.ReactElement | null;
  disabled: boolean;
}

export default function AdminHelpCenterMenu() {
  const location = useLocation();
  const [showFutureAssistance, setShowFutureAssistance] = useState(false);
  const { showPlatformTourReminder, showPlatformTour } =
    useSelector(platformTourSelector);
  const dispatch = useDispatch();
  const hasTour = () => {
    const path = location.pathname;
    return (
      !path.includes('/settings') &&
      !path.includes('/logs') &&
      !path.includes('/invoices') &&
      !path.includes('/order-history')
    );
  };
  const header: HTMLElement = document.querySelector(
    'h1.admin-page-header-title',
  );
  const { visible } = useSelector(sidenavSelector);
  let options: AdminHelpCenterMenuOption[] = [
    {
      label: 'Platform Tour',
      click: () => {
        if (!hasTour()) {
          dispatch(hideTutorial());
          return;
        }
        header.scrollIntoView({ block: 'center' });
        setTimeout(() => {
          const { body } = document;
          const html = document.documentElement;
          const height = Math.max(
            body.scrollHeight,
            body.offsetHeight,
            html.clientHeight,
            html.scrollHeight,
            html.offsetHeight,
          );
          dispatch(runTutorial());
          setTimeout(() => {
            const backdrop: SVGElement = document.querySelector(
              '#walktour-portal-dsm > svg',
            ) as SVGElement;
            if (backdrop) {
              backdrop.style.height = `${height.toString()}px`;
            }
          }, 1);
          // check if walktour element is render if not perform rendering again just to ensure its loaded
          setTimeout(() => {
            const backdrop: SVGElement = document.querySelector(
              '#walktour-portal-dsm > svg',
            ) as SVGElement;
            if (!backdrop) {
              dispatch(hideTutorial());
              setTimeout(() => dispatch(runTutorial()), 100);
            }
          }, 100);
        }, 100);
        setShowFutureAssistance(false);
      },
      icon: <StarOnIcon />,
      disabled: !hasTour(),
    },
    {
      label: 'Email Support',
      click: () => window.open('mailto:PartnerSupport@pmi.org'),
      icon: null,
      disabled: false,
    },
    {
      label: 'Learner Guide',
      click: () => window.open(HUB_HELPURL, '_blank'),
      icon: null,
      disabled: false,
    },
    {
      label: 'Admin Guide',
      click: () => window.open(ADMIN_HELPURL, '_blank'),
      icon: null,
      disabled: false,
    },
  ];

  // REMOVE Platform Tour button for now in mobile view
  // TODO: remove this filter when mobile for coachmarks are supported
  if (visible) {
    options = options.filter((x) => x.label !== 'Platform Tour');
  }

  useEffect(() => {
    setShowFutureAssistance(showPlatformTourReminder);
  }, [showPlatformTourReminder, showPlatformTour]);

  return (
    <div>
      <div className="d-block">
        {showFutureAssistance &&
          !sessionStorage.getItem(PLATFORM_TOUR_SESSION_STORAGE_KEY) && (
            <Popover
              arrow
              description="You can pick up where you left off or start the tour over by clicking on the tutorial link here on any page."
              link={{
                href: '#',
                label: 'Got it!',
                onClick: () => {
                  setShowFutureAssistance(false);
                  sessionStorage.setItem(
                    PLATFORM_TOUR_SESSION_STORAGE_KEY,
                    '1',
                  );
                },
              }}
              placement="top-start"
              targetProps={{
                role: 'button',
              }}
              theme="dark"
              title="Future assistance"
              show
              className={styles.futureAssistancePopover}
              onToggle={() => {
                dispatch(hidePopupReminder());
                sessionStorage.setItem(PLATFORM_TOUR_SESSION_STORAGE_KEY, '1');
              }}
            >
              <span />
            </Popover>
          )}
        <Button
          titleText=""
          className="d-none hidden helpGuide-startCoachMarkTour"
          onClick={() => dispatch(runTutorial())}
        />
        <div className={styles.helpCenterMenu}>
          <Selector
            withIconLabel
            label="Help Center"
            customBtn={{
              titleText: 'Help Center',
              icon: QuestionIcon,
              variant: 'outline-secondary',
              className: styles.helpCenterMenuSelectorBtn,
            }}
            className={styles.helpCenterMenuSelector}
          >
            <div className="d-block p-3 position-relative z-3">
              {options.map((option, index) => (
                <LinkButton
                  size="md"
                  key={`helpCenterItem${index.toString()}`}
                  className={`${styles.helpCenterMenuItem} px-3`}
                  titleText={option.label}
                  onClick={() => option.click()}
                  disabled={option.disabled}
                  icon={() => option.icon}
                />
              ))}
            </div>
          </Selector>
        </div>
      </div>
      <p className="desc">
        Visit our help section for guidance and quick solutions.
      </p>
    </div>
  );
}
